import { CheckCircle as UiCheckCircle, XCircle as UiXCircle } from '../components/icons';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import persianDate from 'persian-date';

const formatNumber = (value, isGold = false) => {
  if (!value || isNaN(parseFloat(value))) return '0';
  const number = parseFloat(value);
  return isGold
    ? number.toLocaleString('fa-IR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    : number.toLocaleString('fa-IR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

export default function WalletRequests() {
  const API_URL = process.env.REACT_APP_API_URL || '/';
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      if (err.response?.status !== 401 && localStorage.getItem('refreshToken')) throw err;
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  const fetchData = async () => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.get('/api/wallet/requests/user', {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setRequests(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          const res = await api.get('/api/wallet/requests/user', {
            headers: { Authorization: `Bearer ${newToken}` }
          });
          setRequests(res.data);
        } else {
          setError('جلسه شما منقضی شده است. لطفاً دوباره وارد شوید.');
          setTimeout(() => navigate('/login_page'), 2000);
        }
      } else {
        setError(err.response?.data?.message || 'خطا در دریافت اطلاعات');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const handleCancelRequest = async (requestId) => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.post(
        `/api/wallet/requests/cancel/${requestId}`,
        {},
        { headers: { Authorization: `Bearer ${newToken}` } }
      );
      setSuccess(res.data.message);
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در لغو درخواست');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getStatusText = (status) => {
    const map = {
      pending: 'در انتظار تأیید',
      approved: 'تأیید شده',
      rejected: 'رد شده',
      canceled: 'لغو شده'
    };
    return map[status] || status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': case 'canceled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeText = (type) => {
    return type === 'deposit' ? 'شارژ' : 'برداشت';
  };

  const getTypeColor = (type) => {
    return type === 'deposit' ? 'text-green-700' : 'text-red-700';
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
        <div className="max-w-4xl mx-auto">

          {/* پیام‌ها */}
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
            <AnimatePresence>
              {error && (
                <motion.div initial={{ y: -100 }} animate={{ y: 0 }} exit={{ y: -100 }} transition={{ type: "spring" }}>
                  <div className="bg-red-500/90 backdrop-blur-lg text-white px-6 py-4 rounded-2xl shadow-2xl border border-red-300 flex items-center gap-3">
                    <UiXCircle className="w-6 h-6" />
                    <span className="font-medium">{error}</span>
                  </div>
                </motion.div>
              )}
              {success && (
                <motion.div initial={{ y: -100 }} animate={{ y: 0 }} exit={{ y: -100 }} transition={{ type: "spring" }}>
                  <div className="bg-green-500/90 backdrop-blur-lg text-white px-6 py-4 rounded-2xl shadow-2xl border border-green-300 flex items-center gap-3">
                    <UiCheckCircle className="w-6 h-6" />
                    <span className="font-medium">{success}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* عنوان */}
          <motion.h2 data-page-title="true"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-10"
          >
            درخواست‌های کیف پول
          </motion.h2>

          {/* جدول دسکتاپ */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="hidden md:block bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">لیست درخواست‌های شما</h3>
            </div>
            {requests.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-500">هیچ درخواستی ثبت نشده</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-right">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>

                      <th className="px-4 py-3 font-semibold">نوع</th>
                      <th className="px-4 py-3 font-semibold">تاریخ</th>
                      <th className="px-4 py-3 font-semibold">مبلغ (ریال)</th>
                      <th className="px-4 py-3 font-semibold">وضعیت</th>
                      <th className="px-4 py-3 font-semibold">دلیل رد</th>
                      <th className="px-4 py-3 font-semibold">تصویر فیش</th>
                      
                      <th className="px-4 py-3 font-semibold">عملیات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {requests.map((req, i) => (
                      <motion.tr
                        key={req.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className={req.status === 'pending' ? 'bg-yellow-50' : 'hover:bg-indigo-50/50'}
                      >
                        <td className={`px-4 py-3 font-bold ${getTypeColor(req.type)}`}>
                          {getTypeText(req.type)}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {new persianDate(new Date(req.created_at)).format('YY/MM/DD HH:mm')}
                        </td>
                        <td className="px-4 py-3 font-mono text-indigo-700">{formatNumber(req.amount)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(req.status)}`}>
                            {getStatusText(req.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{req.reject_reason || '—'}</td>
                        <td className="px-4 py-3">
                          {req.receipt_image ? (
                            <a
                            href={`${API_URL}${req.receipt_image}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-800 font-medium underline"
                            >
                              مشاهده
                            </a>
                          ) : '—'}
                        </td>
                       
                        <td className="px-4 py-3">
                          {req.type === 'withdraw' && req.status === 'pending' && (
                            <button
                              onClick={() => handleCancelRequest(req.id)}
                              className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:from-red-600 hover:to-rose-700 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                            >
                              لغو
                            </button>
                          )}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>

          {/* کارت‌های موبایل */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="md:hidden space-y-4"
          >
            {requests.length === 0 ? (
              <div className="bg-white rounded-3xl p-8 text-center shadow-lg border border-gray-200">
                <p className="text-gray-500">هیچ درخواستی ثبت نشده</p>
              </div>
            ) : (
              requests.map((req, i) => (
                <motion.div
                  key={req.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-3xl p-5 shadow-lg border border-gray-200"
                >
                  <div className="flex justify-between items-start mb-3">
                  <div>
                      <p className="text-gray-500">تاریخ</p>
                      <p className="text-gray-600">{new persianDate(new Date(req.created_at)).format('YY/MM/DD HH:mm')}</p>
                    </div>
                    <div>
                      <p className={`font-bold text-lg ${getTypeColor(req.type)}`}>{getTypeText(req.type)}</p>
                      <p className="text-sm text-gray-600">مبلغ: {formatNumber(req.amount)} ریال</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(req.status)}`}>
                      {getStatusText(req.status)}
                    </span>
                  </div>

                  {req.reject_reason && (
                    <p className="text-xs text-red-600 mb-2">دلیل رد: {req.reject_reason}</p>
                  )}

                  <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                  {req.receipt_image && (
  <div>
    <p className="text-gray-500">تصویر فیش</p>
    <a
      href={`${API_URL}${req.receipt_image}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-indigo-600 hover:text-indigo-800 font-medium underline"
    >
      مشاهده
    </a>
  </div>
)}
                    
                  </div>

                  {req.type === 'withdraw' && req.status === 'pending' && (
                    <button
                      onClick={() => handleCancelRequest(req.id)}
                      className="w-full mt-4 bg-gradient-to-r from-red-500 to-rose-600 text-white py-2.5 rounded-xl font-medium hover:from-red-600 hover:to-rose-700 shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all"
                    >
                      لغو درخواست
                    </button>
                  )}
                </motion.div>
              ))
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}